import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import texasApi from '../../services/texasApi';
import PRSWizard from '../Properties/components/PRSWizard';

const PROPERTY_TYPES = [
  'SINGLE_FAMILY',
  'DUPLEX',
  'TRIPLEX',
  'FOURPLEX',
  'MULTIFAMILY',
  'CONDO',
  'COMMERCIAL_RESIDENTIAL',
];

interface RegisterFormState {
  propertyId: string;
  propertyAddress: string;
  dcadId: string;
  propertyType: string;
  unitCount: string;
  yearBuilt: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
}

const EMPTY_FORM: RegisterFormState = {
  propertyId: '',
  propertyAddress: '',
  dcadId: '',
  propertyType: 'SINGLE_FAMILY',
  unitCount: '1',
  yearBuilt: '',
  ownerName: '',
  ownerEmail: '',
  ownerPhone: '',
};

export const MunicipalRegistrationsHub: React.FC = () => {
  const navigate = useNavigate();
  const [dallasRegs, setDallasRegs] = useState<any[]>([]);
  const [registrationScore, setRegistrationScore] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  // BUG-2: create/register entry point — a working "New Registration" CTA.
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm] = useState<RegisterFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // BUG-4: the Texas Registration Wizard must open a modal on the hub too.
  const [showWizard, setShowWizard] = useState(false);

  const loadRegistrations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regsRes, scoreRes] = await Promise.allSettled([
        texasApi.dallasRegistration.getRenewals(),
        texasApi.dallasRegistrationScore.getPortfolioSummary(),
      ]);
      if (regsRes.status === 'fulfilled') {
        const d: any = regsRes.value;
        // API returns { success, data: { alertWindows, summary, renewals: [...] } }.
        // Dig out the array so the hub never stores an envelope (which crashed
        // .filter/.length and blanked the whole page — BUG-REG-HUB-01).
        const payload = d?.data?.data ?? d?.data ?? d;
        const list = Array.isArray(payload)
          ? payload
          : (Array.isArray(payload?.renewals)
            ? payload.renewals
            : (Array.isArray(payload?.items) ? payload.items : []));
        setDallasRegs(list);
      }
      if (scoreRes.status === 'fulfilled') {
        setRegistrationScore(scoreRes.value);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load registrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRegistrations();
  }, [loadRegistrations]);

  const openRegister = () => {
    setSubmitError(null);
    setForm(EMPTY_FORM);
    setShowRegister(true);
  };

  const submitRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.propertyId.trim()) {
      setSubmitError('Property ID is required');
      return;
    }
    setSaving(true);
    setSubmitError(null);
    try {
      const res: any = await texasApi.dallasRegistration.register({
        propertyId: form.propertyId.trim(),
        propertyAddress: form.propertyAddress.trim(),
        dcadId: form.dcadId.trim(),
        propertyType: form.propertyType,
        unitCount: parseInt(form.unitCount || '1', 10) || 1,
        yearBuilt: form.yearBuilt.trim() ? parseInt(form.yearBuilt.trim(), 10) || undefined : undefined,
        owner: {
          name: form.ownerName.trim(),
          email: form.ownerEmail.trim(),
          phone: form.ownerPhone.trim(),
        },
      });
      const data = res?.data ?? res ?? {};
      setSaved(data?.message || `Registration submitted for ${form.propertyId.trim()}`);
      setShowRegister(false);
      loadRegistrations();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || err?.message || 'Registration failed');
    } finally {
      setSaving(false);
    }
  };

  const expiringCount = dallasRegs.filter((r: any) => r.status === 'expiring').length;
  const expiredCount = dallasRegs.filter((r: any) => r.status === 'expired').length;
  const activeCount = dallasRegs.filter((r: any) => r.status === 'active').length;

  return (
    <div data-testid="municipal-registrations-hub" className="texas-page-container">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1>Municipal Registrations Hub</h1>
          <p className="page-description">
            Dallas Ch. 27 annual registration & Houston STR compliance.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100"
            data-testid="registration-wizard-btn"
          >
            <i className="fas fa-clipboard-check mr-1.5"></i> Texas Registration Wizard
          </button>
          <button
            type="button"
            onClick={openRegister}
            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-emerald-600"
            data-testid="new-registration-btn"
          >
            <i className="fas fa-plus mr-1.5"></i> Register Property
          </button>
        </div>
      </div>

      {saved && <div className="saved-banner" data-testid="saved-banner">{saved}</div>}

      {!loading && (
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-red-600 tracking-wider">EXPIRED</div>
            <div className="text-3xl font-black text-red-600 mt-1">{expiredCount}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-amber-600 tracking-wider">EXPIRING SOON</div>
            <div className="text-3xl font-black text-amber-600 mt-1">{expiringCount}</div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="text-xs font-bold text-slate-500 tracking-wider">ACTIVE</div>
            <div className="text-3xl font-black text-emerald-600 mt-1">{activeCount}</div>
          </div>
        </section>
      )}

      {error && <div className="error" data-testid="error">{error}</div>}

      {registrationScore && (
        <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 mb-4">
          <h2>Registration Score Summary</h2>
          <div className="score-display">
            <span className="text-2xl font-black">{registrationScore.score ?? '—'}/100</span>
            {registrationScore.pillarBreakdown && (
              <div className="pillar-breakdown">
                {registrationScore.pillarBreakdown.map((p: any, i: number) => (
                  <div key={i} className="pillar-row">
                    <span>{p.name}</span>
                    <span>{p.score}/{p.maxScore}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <h2>Dallas Registrations</h2>
        {dallasRegs.length === 0 ? (
          <div className="no-results">
            <p className="mb-2">No registrations on file</p>
            <button
              type="button"
              onClick={openRegister}
              className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-slate-800 hover:bg-emerald-600"
              data-testid="empty-state-register-btn"
            >
              <i className="fas fa-plus mr-1.5"></i> Register your first Dallas property
            </button>
          </div>
        ) : (
          <table className="registration-table">
            <thead>
              <tr>
                <th>Property ID</th>
                <th>Certificate #</th>
                <th>Status</th>
                <th>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {dallasRegs.map((r: any, idx: number) => (
                <tr key={idx} data-testid={`registration-${idx}`}>
                  <td>{r.propertyId}</td>
                  <td>{r.certificateNumber || '—'}</td>
                  <td>{r.status}</td>
                  <td>{r.expiryDate || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showRegister && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto bg-gray-900 bg-opacity-50"
          onClick={(e) => { if (e.target === e.currentTarget) setShowRegister(false); }}
          data-testid="register-property-modal"
        >
          <div className="flex items-start justify-center min-h-screen px-4 pt-10 pb-20">
            <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-gray-900">Register a Dallas Property (Ch. 27)</h3>
                <button onClick={() => setShowRegister(false)} className="text-gray-400 hover:text-gray-600">
                  <i className="fas fa-times"></i>
                </button>
              </div>
              {submitError && (
                <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {submitError}
                </div>
              )}
              <form onSubmit={submitRegister} className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Property ID *</label>
                    <input
                      type="text"
                      value={form.propertyId}
                      onChange={(e) => setForm({ ...form, propertyId: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-property-id"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">DCAD Account #</label>
                    <input
                      type="text"
                      value={form.dcadId}
                      onChange={(e) => setForm({ ...form, dcadId: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-dcad"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-gray-600">Property Address</label>
                    <input
                      type="text"
                      value={form.propertyAddress}
                      onChange={(e) => setForm({ ...form, propertyAddress: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-address"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Property Type</label>
                    <select
                      value={form.propertyType}
                      onChange={(e) => setForm({ ...form, propertyType: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-type"
                    >
                      {PROPERTY_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Units</label>
                    <input
                      type="number"
                      min={1}
                      value={form.unitCount}
                      onChange={(e) => setForm({ ...form, unitCount: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-units"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Year Built</label>
                    <input
                      type="number"
                      min={1800}
                      value={form.yearBuilt}
                      onChange={(e) => setForm({ ...form, yearBuilt: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-year"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Owner Name</label>
                    <input
                      type="text"
                      value={form.ownerName}
                      onChange={(e) => setForm({ ...form, ownerName: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-owner-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Owner Email</label>
                    <input
                      type="email"
                      value={form.ownerEmail}
                      onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-owner-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600">Owner Phone</label>
                    <input
                      type="tel"
                      value={form.ownerPhone}
                      onChange={(e) => setForm({ ...form, ownerPhone: e.target.value })}
                      className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                      data-testid="reg-form-owner-phone"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowRegister(false)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg text-sm font-bold bg-slate-800 text-white hover:bg-emerald-600 disabled:opacity-50"
                    data-testid="reg-submit-btn"
                  >
                    {saving ? 'Registering...' : 'Submit Registration'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <PRSWizard isOpen={showWizard} onClose={() => setShowWizard(false)} />
    </div>
  );
};

export default MunicipalRegistrationsHub;